import { jsx, jsxs } from "react/jsx-runtime";
import * as React from "react";
import { forwardRef } from "react";
import styled, { keyframes, css } from "styled-components";
import { semanticColors, colors, cbSemanticCssVars, typographyCss } from "@creditornot/cb-ingredients";
import { createCssVars, createCssVarDefinitions, createThemedCssVarDefinitions } from "@creditornot/cb-utils";
import { shade } from "polished";
const SvgCheck = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M23.156 2.406a2.052 2.052 0 00-2.888.442L9.976 16.943 3.39 11.627a2.051 2.051 0 00-2.063-.348 2.093 2.093 0 00-.517 3.602l8.263 6.67a2.03 2.03 0 001.29.449 2.066 2.066 0 001.663-.834l11.567-15.84a2.097 2.097 0 00-.438-2.92z" }) }));
function mergeRefs(refs) {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(value);
      } else if (ref != null) {
        ref.current = value;
      }
    });
  };
}
const colorsConfig = {
  border: {
    light: semanticColors.light.borderLight,
    lightHighContrast: semanticColors.lightHighContrast.borderLight,
    dark: colors.salt32
  },
  bgUnchecked: {
    light: semanticColors.light.surfaceMain,
    lightHighContrast: semanticColors.light.surfaceMain,
    dark: semanticColors.dark.surfaceMain
  },
  bgUncheckedDisabled: {
    light: colors.pepper12,
    lightHighContrast: colors.pepper12,
    dark: colors.salt20
  },
  bgChecked: {
    light: colors.wolt100,
    lightHighContrast: colors.darkSky100,
    dark: colors.wolt100
  },
  bgCheckedDisabled: {
    light: colors.pepper12,
    lightHighContrast: colors.pepper12,
    dark: colors.salt12
  },
  icon: {
    light: colors.salt100,
    lightHighContrast: colors.salt100,
    dark: colors.pepper100
  },
  iconDisabled: {
    light: colors.pepper40,
    lightHighContrast: colors.pepper64,
    dark: colors.salt48
  }
};
const cssVars = createCssVars(["size", "borderRadius", "borderWidth", "iconSize"]);
const colorCssVars$1 = createCssVars([
  "border",
  "bgUnchecked",
  "bgUncheckedDisabled",
  "bgChecked",
  "bgCheckedDisabled",
  "icon",
  "iconDisabled"
]);
const zoomIn = keyframes`
  0% {
    transform: scale(0.4);
  }

  75% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
`;
const zoomOut = keyframes`
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(0.4);
  }
`;
const Root$1 = styled.div`
  ${createCssVarDefinitions({
  size: "16px",
  borderRadius: "2px",
  borderWidth: "1px",
  iconSize: `calc(${10 / 16} * var(--size))`
})}

  ${createThemedCssVarDefinitions(colorsConfig)}

  flex-shrink: 0;
  position: relative;
  z-index: 0;
  border-radius: ${cssVars.borderRadius};
  display: block;

  width: ${cssVars.size};
  height: ${cssVars.size};
`;
const BG = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  border-radius: ${cssVars.borderRadius};
  border-style: solid;
  border-width: ${cssVars.borderWidth};
  border-color: ${colorCssVars$1.border};
  background-color: ${colorCssVars$1.bgUnchecked};
  transition:
    background-color 100ms ease-in,
    border-color 100ms ease-in,
    opacity 100ms ease-in,
    border-width 100ms ease-in;
`;
const CheckmarkIcon = styled(SvgCheck)`
  /* increased specifity in case a component is using generic svg selector to set or override icon colors */
  && {
    fill: ${colorCssVars$1.icon};
  }
  width: ${cssVars.iconSize};
`;
const PartialCheckIcon = styled.div`
  background-color: ${colorCssVars$1.icon};
  display: inline-block;
  width: ${cssVars.iconSize};
  height: calc(0.2 * ${cssVars.iconSize});
  border-radius: ${cssVars.borderRadius};
`;
const iconContainerStyle = css`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 200ms ease-in;
  opacity: 0;
`;
const CheckedIconContainer = styled.div`
  ${iconContainerStyle}
`;
const IndeterminateIconContainer = styled.div`
  ${iconContainerStyle}
`;
const Input = styled.input`
  margin: 0;
  opacity: 0;
  outline: none;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  box-sizing: border-box;
  width: 100%;
  height: 100%;

  &:disabled {
    cursor: not-allowed;
  }

  &:disabled ~ ${CheckedIconContainer} > ${CheckmarkIcon} {
    fill: ${colorCssVars$1.iconDisabled};
  }

  &:disabled ~ ${CheckedIconContainer} > ${PartialCheckIcon} {
    background-color: ${colorCssVars$1.iconDisabled};
  }

  &:checked ~ ${BG}, &:indeterminate ~ ${BG} {
    background-color: ${colorCssVars$1.bgChecked};
  }

  &:disabled ~ ${BG} {
    background-color: ${colorCssVars$1.bgUncheckedDisabled};
  }

  &:disabled:checked ~ ${BG}, &:disabled:indeterminate ~ ${BG} {
    background-color: ${colorCssVars$1.bgCheckedDisabled};
  }

  &:checked:not(:disabled) ~ ${BG}, &:indeterminate:not(:disabled) ~ ${BG} {
    border-width: 0;
  }

  &:checked ~ ${CheckedIconContainer}, &:indeterminate ~ ${IndeterminateIconContainer} {
    opacity: 1;
  }

  &:checked ~ ${CheckedIconContainer} > ${CheckmarkIcon} {
    animation: ${zoomIn} 300ms;
  }

  &:not(:checked) ~ ${CheckedIconContainer} > ${CheckmarkIcon} {
    animation: ${zoomOut} 300ms;
  }

  &:indeterminate ~ ${IndeterminateIconContainer} > ${PartialCheckIcon} {
    animation: ${zoomIn} 300ms;
  }

  &:not(:indeterminate) ~ ${IndeterminateIconContainer} > ${PartialCheckIcon} {
    animation: ${zoomOut} 300ms;
  }

  ${({ $disableFocusAndActiveStyles }) => !$disableFocusAndActiveStyles && css`
      /* change background on click */
      &:not(:disabled):active ~ ${BG} {
        background-color: ${colors.pepper8};
      }

      &:not(:disabled):checked:active ~ ${BG}, &:not(:disabled):indeterminate:active ~ ${BG} {
        background-color: ${shade(0.2)(colors.wolt100)};
      }

      :focus-visible ~ ${BG} {
        box-shadow: 0 0 0 2px ${cbSemanticCssVars.a11yFocusIndicator};
        outline: 2px transparent solid;
      }
    `}
`;
const InputCheckbox = ({
  checked,
  className,
  disabled,
  disableFocusAndActiveStyles = false,
  inputClassName,
  inputStyle,
  partiallyChecked = false,
  style,
  ...rest
}, ref) => /* @__PURE__ */ jsxs(Root$1, { className, style, children: [
  /* @__PURE__ */ jsx(
    Input,
    {
      $disableFocusAndActiveStyles: disableFocusAndActiveStyles,
      "aria-checked": partiallyChecked ? "mixed" : checked,
      ...rest,
      checked,
      className: inputClassName,
      disabled,
      ref: mergeRefs([
        ref,
        (node) => {
          if (node) {
            node.indeterminate = partiallyChecked && !checked;
          }
        }
      ]),
      style: inputStyle,
      type: "checkbox"
    }
  ),
  /* @__PURE__ */ jsx(BG, {}),
  /* @__PURE__ */ jsx(CheckedIconContainer, { children: /* @__PURE__ */ jsx(CheckmarkIcon, {}) }),
  /* @__PURE__ */ jsx(IndeterminateIconContainer, { children: /* @__PURE__ */ jsx(PartialCheckIcon, {}) })
] });
const InputCheckbox$1 = forwardRef(InputCheckbox);
const colorCssVars = createCssVars(["label", "labelActive"]);
const Root = styled.div`
  ${createThemedCssVarDefinitions({
  label: {
    light: colors.pepper100,
    dark: colors.salt100
  },
  labelActive: {
    light: colors.pepper80,
    dark: colors.salt80
  }
})}

  position: relative;
`;
const TextLabel = styled.span`
  ${typographyCss.Body3()}
  color: ${colorCssVars.label};
`;
const Label = styled.label`
  display: flex;
  align-items: center;

  &:active ${TextLabel} {
    color: ${colorCssVars.labelActive};
  }
`;
const Content = styled.span`
  margin-inline-start: 8px;
`;
const Checkbox = React.forwardRef(
  ({
    checked,
    children,
    className,
    disabled,
    id,
    inputProps,
    name,
    onBlur,
    onChange,
    onClick,
    onFocus,
    partiallyChecked,
    readOnly,
    required,
    rootRef,
    style,
    value
  }, ref) => /* @__PURE__ */ jsx(Root, { className, ref: rootRef, style, children: /* @__PURE__ */ jsxs(Label, { htmlFor: id, children: [
    /* @__PURE__ */ jsx(
      InputCheckbox$1,
      {
        ...inputProps,
        checked,
        disabled,
        id,
        name,
        onBlur,
        onChange,
        onClick,
        onFocus,
        partiallyChecked,
        readOnly,
        ref,
        required,
        value
      }
    ),
    children !== void 0 && /* @__PURE__ */ jsx(Content, { children: typeof children === "string" ? /* @__PURE__ */ jsx(TextLabel, { children }) : children })
  ] }) })
);
Checkbox.displayName = "Checkbox";
export {
  Checkbox,
  InputCheckbox$1 as InputCheckbox
};
//# sourceMappingURL=index.js.map
